import { useCallback, useMemo } from "react";
import { head, map, orderBy, size, sumBy, zipWith } from "lodash";
import { Item } from "@charty/legend/item/model";
import { darken } from "@glossy";
import { content } from "@glossy/theme";
import { GlossyTheme } from "@glossy/theme/types";
import { Range } from "@linkfluence/models/date";
import { endOf, getDatesFromRange, startOf } from "@tootools/date";
import { natural } from "@tootools/sort";
import { Serie } from "../models";
import { Sort } from ".";

const includeEndOfRange = (data, range) =>
  size(data) > size(getDatesFromRange(range));
export function useFormatData(
  sortedSeries: Serie[],
  theme: GlossyTheme,
  range: Range
) {
  return useMemo(() => {
    const withEndOfRange = includeEndOfRange(head(sortedSeries)?.data, range);
    const labels = map(getDatesFromRange(range, withEndOfRange), (date) =>
      date.toISOString()
    );
    return {
      labels,
      datasets: map(sortedSeries, ({ data, color, label, id }) => {
        return {
          id,
          data: zipWith(labels, data, (label, val) => (val ? val.count : 0)),
          backgroundColor: color,
          label,
          accentColor: color,
          accentFadedColor: content({ theme }, "tertiary"),
          hoverBackgroundColor: darken(0.1, color),
        };
      }),
    };
  }, [range, sortedSeries, theme]);
}

export function useSortSeries(sort: Sort, series: Serie[]) {
  return useMemo(() => {
    return sort === "alpha"
      ? natural(series, "label")
      : sort === "mainValue"
      ? orderBy(
          series,
          (serie) => {
            return sumBy(serie.data, "count");
          },
          "desc"
        )
      : series;
  }, [series, sort]);
}

export function useLegendSeries(sortedSeries: Serie[]): Item[] {
  return useMemo(
    () =>
      map(sortedSeries, (serie) => {
        return {
          id: serie.id,
          label: serie.label,
          count: sumBy(serie.data, "count"),
          color: serie.color,
        };
      }),
    [sortedSeries]
  );
}

export function useOnClickBar(legendSeries, range, onClickBar) {
  return useCallback(
    (datasetIndex: number, index: number) => {
      const date = getDatesFromRange(range)[index];
      onClickBar(legendSeries[datasetIndex].id, {
        from: startOf(date, range.interval).toISOString(),
        to: endOf(date, range.interval).toISOString(),
      });
    },
    [legendSeries, onClickBar, range]
  );
}

export function useTotal(total: number, legendSeries) {
  return useMemo(() => {
    if (total) {
      return total;
    } else {
      return sumBy(legendSeries, "count");
    }
  }, [legendSeries, total]);
}

export function updateBarChartOnHover(chart, hovered) {
  for (let i = 0; i < chart.data.datasets.length; i++) {
    const bar = chart.data.datasets[i];
    if (hovered) {
      if (bar.id === hovered) {
        bar.backgroundColor = bar.accentColor;
      } else {
        bar.backgroundColor = bar.accentFadedColor;
      }
    } else {
      bar.backgroundColor = bar.accentColor;
    }
  }
}
